// Systémové okno „Vybrat soubor“ – jedno pro všechny aplikace. Dva režimy:
//   storage: výběr z úložiště (strom + seznam, filtr typů, Nahrát → dialog „Kam uložit“, přetažení souboru dovnitř)
//   upload:  jednorázové nahrání do dočasného prostoru (aplikace soubor zpracuje nebo si ho přesune k sobě)
// Aplikace nikdy nepoužívají <input type=file> samy – volají pickFile().
import { core, upload, ApiError } from '../api';
import { SHARED_ROOT, type StorageItem } from '../contract';
import { h, clear } from '../dom';
import { t, tk, fmtDateTime, fmtSize } from '../i18n';
import { icon, fileIcon } from '../icons';
import { IwSplit, IwTree, type TreeNode, statusBar, emptyState } from '../components/misc';
import { IwRibbon } from '../components/ribbon';
import { IwAddressBar } from '../components/addressbar';
import { dialog } from '../components/dialog';
import { toast } from '../components/toast';
import { uploadFiles } from './uploads';
import { dropHighlight } from './dropzone';
import type { WindowManager } from '../wm/manager';
import type { IwWindow } from '../wm/window';

export interface PickOptions {
  /** storage = soubor z úložiště, upload = soubor ze zařízení, folder = cílová složka (Přesunout do…) */
  mode: 'storage' | 'upload' | 'folder';
  /** MIME vzory, např. ['image/*', 'application/pdf'] */
  accept?: string[];
  multiple?: boolean;
  /** Výchozí složka pro Nahrát (název v domovském prostoru, např. 'Obrázky') */
  defaultFolder?: string;
  title?: string;
  parent?: IwWindow | null;
}

let wmRef: WindowManager | null = null;

export function configurePicker(wm: WindowManager): void {
  wmRef = wm;
}

function matches(item: StorageItem, accept?: string[]): boolean {
  if (!accept || !accept.length || item.kind === 'folder') return true;
  const mime = item.mime ?? '';
  return accept.some((p) => p === mime || (p.endsWith('/*') && mime.startsWith(p.slice(0, -1))));
}

/** Výběr cílové složky (Přesunout do…): vrátí složku, nebo null při stornu. */
export async function pickFolder(opts: Omit<PickOptions, 'mode'> = {}): Promise<{ id: string; name: string } | null> {
  const r = await pickFile({ ...opts, mode: 'folder', title: opts.title ?? t('core.picker.pick_folder') });
  const f = r?.[0];
  return f ? { id: f.id, name: f.name } : null;
}

/** Vrátí vybrané položky úložiště, nebo null při stornu. */
export async function pickFile(opts: PickOptions): Promise<StorageItem[] | null> {
  if (opts.mode === 'upload') return pickUpload(opts);
  const wm = wmRef;
  if (!wm) return null;
  return new Promise((resolve) => {
    let done = false;
    const finish = (r: StorageItem[] | null) => {
      if (done) return;
      done = true;
      resolve(r);
    };
    void wm.openLocal({
      title: opts.title ?? t('core.picker.title'),
      icon: 'folder-open',
      size: { w: 860, h: 560 },
      modal: true,
      parent: opts.parent ?? null,
      build: (win, close) => new PickerView(opts, win, (r) => {
        finish(r);
        close();
      }),
      onClosed: () => finish(null),
    });
  });
}

async function pickUpload(opts: PickOptions): Promise<StorageItem[] | null> {
  return new Promise((resolve) => {
    const input = h('input', { type: 'file', hidden: true, multiple: Boolean(opts.multiple), accept: opts.accept?.join(',') ?? null });
    input.addEventListener('change', async () => {
      const files = input.files;
      input.remove();
      if (!files?.length) return resolve(null);
      const items = await uploadFiles('', files, undefined, true);
      resolve(items.length ? items : null);
    });
    input.addEventListener('cancel', () => {
      input.remove();
      resolve(null);
    });
    document.body.appendChild(input);
    input.click();
  });
}

class PickerView {
  readonly el: HTMLElement;
  private tree = new IwTree();
  private ribbon = new IwRibbon();
  private list = h('div', { class: 'picker-list', tabindex: '0' });
  private address = new IwAddressBar();
  private history: string[] = [];
  private forward: string[] = [];
  private pathIds: string[] = [];
  private status = h('div');
  private folderId = '';
  private items: StorageItem[] = [];
  private selected = new Set<string>();
  private roots: { user: StorageItem; shared: StorageItem } | null = null;
  private currentName = '';
  private readonly folderMode: boolean;
  private okBtn = h('button', { type: 'button', class: 'btn primary' }, t('core.ui.ok'));
  private layoutObserver: ResizeObserver;

  constructor(private opts: PickOptions, private win: IwWindow, private finish: (r: StorageItem[] | null) => void) {
    this.folderMode = opts.mode === 'folder';
    const split = new IwSplit();
    split.first.appendChild(this.tree);
    const search = h('input', { class: 'input search', type: 'search', placeholder: t('core.ui.search_placeholder') });
    search.addEventListener('input', () => this.renderList(search.value));
    this.ribbon.right().appendChild(h('div', { class: 'search-wrap' }, icon('search'), search));
    this.refreshRibbon();
    split.second.append(this.list, this.status);
    split.setWidth(200);
    // strom podle šířky okna (viz Soubory): úzké okno = schovaný, vysouvá se přes seznam
    let narrow: boolean | null = null;
    let treeHidden = false;
    const setTree = (hidden: boolean) => {
      treeHidden = hidden;
      split.setFirstHidden(hidden);
      this.address.setTreeShown(!hidden);
    };
    this.layoutObserver = new ResizeObserver(() => {
      const w = this.el.clientWidth;
      const n = w < 700;
      if (!w || n === narrow) return;
      narrow = n;
      split.setOverlay(n);
      setTree(n);
    });
    this.address.onTreeToggle(() => setTree(!treeHidden));
    const cancel = h('button', { type: 'button', class: 'btn' }, t('core.ui.cancel'));
    cancel.addEventListener('click', () => finish(null));
    this.okBtn.disabled = true;
    this.okBtn.addEventListener('click', () => this.confirm());
    this.el = h('div', { class: 'view picker-view' }, this.ribbon, this.address, split, h('div', { class: 'dialog-buttons picker-buttons' }, cancel, this.okBtn));
    this.layoutObserver.observe(this.el);
    this.tree.onSelect = (n) => {
      void this.navigate(n.id);
      if (narrow) setTree(true);
    };
    this.tree.onExpand = async (node) => {
      const r = node.id === SHARED_ROOT ? await core<{ items: StorageItem[] }>('storage', 'sharedWithMe') : await core<{ items: StorageItem[] }>('storage', 'children', { id: node.id });
      return r.items.filter((i) => i.kind === 'folder').map((i) => ({ id: i.id, label: i.name, icon: 'folder', children: i.hasFolders ? null : [] }));
    };
    this.installDrop();
    void this.init();
  }

  private async init(): Promise<void> {
    const r = await core<{ user: StorageItem; shared: StorageItem }>('storage', 'roots');
    this.roots = r;
    const nodes: TreeNode[] = [
      { id: r.user.id, label: t('soubory.my'), icon: 'home', children: r.user.hasFolders === false ? [] : null },
      { id: SHARED_ROOT, label: t('soubory.shared_me'), icon: 'users', children: null },
      { id: r.shared.id, label: t('soubory.shared'), icon: 'building', children: r.shared.hasFolders === false ? [] : null },
    ];
    this.tree.setNodes(nodes);
    void this.tree.expand(r.user.id);
    let start = r.user.id;
    if (this.opts.defaultFolder) {
      try {
        const f = await core<{ item: StorageItem }>('storage', 'folder', { name: this.opts.defaultFolder });
        start = f.item.id;
      } catch {
        /* výchozí složka není nutná */
      }
    }
    await this.navigate(start);
  }

  private refreshRibbon(): void {
    this.ribbon.setButtons([{ id: 'upload', label: t('core.picker.upload'), icon: 'upload', group: 'core.grp.new', onClick: () => void this.uploadDialog() }]);
    this.address.setNav({
      canBack: this.history.length > 0,
      canForward: this.forward.length > 0,
      canUp: this.pathIds.length > 1,
      onBack: () => {
        const prev = this.history.pop();
        if (prev) {
          this.forward.push(this.folderId);
          void this.navigate(prev, false);
        }
      },
      onForward: () => {
        const next = this.forward.pop();
        if (next) {
          this.history.push(this.folderId);
          void this.navigate(next, false);
        }
      },
      onUp: () => {
        const parent = this.pathIds[this.pathIds.length - 2];
        if (parent) void this.navigate(parent);
      },
    });
  }

  private async navigate(id: string, record = true): Promise<void> {
    if (record && this.folderId && this.folderId !== id) {
      this.history.push(this.folderId);
      this.forward.length = 0;
    }
    this.folderId = id;
    this.selected.clear();
    this.okBtn.disabled = true;
    const r = id === SHARED_ROOT
      ? await core<{ items: StorageItem[] }>('storage', 'sharedWithMe').then((x) => ({ items: x.items, path: [{ id: SHARED_ROOT, name: '' }] }))
      : await core<{ items: StorageItem[]; path: { id: string; name: string }[] }>('storage', 'children', { id });
    this.items = r.items.filter((i) => matches(i, this.opts.accept) && (!this.folderMode || i.kind === 'folder'));
    const path = r.path.map((p, i) => ({ id: p.id, name: i === 0 ? (p.id === this.roots?.user.id ? t('soubory.my') : p.id === this.roots?.shared.id ? t('soubory.shared') : p.id === SHARED_ROOT ? t('soubory.shared_me') : p.name) : p.name }));
    this.currentName = path[path.length - 1]?.name ?? '';
    // výběr složky: OK = aktuální složka (ne pohled „Sdílené se mnou“, to není složka)
    if (this.folderMode) this.okBtn.disabled = id === SHARED_ROOT;
    this.address.setPath(path, (id) => void this.navigate(id));
    this.pathIds = r.path.map((p) => p.id);
    this.tree.setChildren(id, r.items.filter((i) => i.kind === 'folder').map((i) => ({ id: i.id, label: i.name, icon: 'folder', children: i.hasFolders ? null : [] })));
    void this.tree.expandPath(this.pathIds.length ? this.pathIds : r.path.map((p) => p.id)).then(() => this.tree.select(id));
    this.tree.select(id);
    this.renderList('');
    this.refreshRibbon();
  }

  private renderList(filter: string): void {
    clear(this.list);
    const q = filter.trim().toLowerCase();
    const items = this.items.filter((i) => !q || i.name.toLowerCase().includes(q));
    if (!items.length) {
      this.list.appendChild(emptyState(this.folderMode ? t('core.picker.no_subfolders') : t('soubory.empty'), 'folder-open'));
      this.status.replaceChildren(statusBar(this.folderMode ? t('core.picker.folder_hint') : t('core.picker.hint')));
      return;
    }
    const table = h(
      'table',
      { class: 't files-table' },
      h('thead', null, h('tr', null, h('th', null, t('soubory.name_col')), h('th', { class: 'w-date' }, t('soubory.modified')), h('th', { class: 'w-size right' }, t('soubory.size')))),
      h(
        'tbody',
        null,
        items.map((it) => {
          const tr = h('tr', { class: this.selected.has(it.id) ? 'selected' : '', dataset: { id: it.id } }, h('td', { class: 'name-cell' }, icon(fileIcon(it.mime, it.isShortcut, it.kind === 'folder')), h('span', null, it.name)), h('td', null, fmtDateTime(it.updatedAt ?? it.createdAt)), h('td', { class: 'right' }, it.kind === 'file' ? fmtSize(it.size) : ''));
          tr.addEventListener('click', (e) => {
            if (it.kind === 'folder' && !this.folderMode) return;
            if (this.opts.multiple && (e.ctrlKey || e.metaKey)) {
              if (this.selected.has(it.id)) this.selected.delete(it.id);
              else this.selected.add(it.id);
            } else this.selected = new Set([it.id]);
            this.list.querySelectorAll<HTMLElement>('tr[data-id]').forEach((r) => r.classList.toggle('selected', this.selected.has(r.dataset['id'] ?? '')));
            this.okBtn.disabled = this.selected.size === 0 && !(this.folderMode && this.folderId !== SHARED_ROOT);
          });
          tr.addEventListener('dblclick', () => {
            if (it.kind === 'folder') void this.navigate(it.id);
            else {
              this.selected = new Set([it.id]);
              this.confirm();
            }
          });
          return tr;
        }),
      ),
    );
    this.list.appendChild(table);
    this.status.replaceChildren(statusBar(this.folderMode ? t('core.picker.folder_hint') : t('core.picker.hint'), t('soubory.items', { count: items.length })));
  }

  private confirm(): void {
    const out = this.items.filter((i) => this.selected.has(i.id));
    if (out.length) return this.finish(out);
    // výběr složky bez označené podsložky = aktuální složka
    if (this.folderMode && this.folderId && this.folderId !== SHARED_ROOT) this.finish([{ id: this.folderId, name: this.currentName, kind: 'folder' } as unknown as StorageItem]);
  }

  /** Nahrát → dialog „Kam uložit“ (výchozí složka od aplikace), po nahrání se soubor vybere. */
  private async uploadDialog(files?: FileList | File[]): Promise<void> {
    if (!files) {
      const input = h('input', { type: 'file', hidden: true, multiple: Boolean(this.opts.multiple), accept: this.opts.accept?.join(',') ?? null });
      const picked = await new Promise<FileList | null>((res) => {
        input.addEventListener('change', () => res(input.files));
        input.addEventListener('cancel', () => res(null));
        document.body.appendChild(input);
        input.click();
      });
      input.remove();
      if (!picked?.length) return;
      files = picked;
    }
    // Kam uložit: aktuální složka, nebo výchozí složka aplikace
    const targets: { id: string; label: string }[] = [];
    const cur = this.address.querySelector('.crumbs')?.textContent ?? '';
    targets.push({ id: this.folderId, label: cur });
    if (this.opts.defaultFolder) {
      const f = await core<{ item: StorageItem }>('storage', 'folder', { name: this.opts.defaultFolder }).catch(() => null);
      if (f && f.item.id !== this.folderId) targets.push({ id: f.item.id, label: t('soubory.my') + ' › ' + f.item.name });
    }
    const sel = h('select', { class: 'input' }, targets.map((x) => h('option', { value: x.id }, x.label)));
    const r = await dialog({
      title: t('core.picker.where'),
      message: h('div', null, h('p', { class: 'muted' }, Array.from(files).map((f) => f.name).join(', ')), h('label', { class: 'field' }, h('span', { class: 'field-label' }, t('core.picker.folder')), sel)),
      buttons: [
        { id: 'cancel', label: t('core.ui.cancel') },
        { id: 'ok', label: t('core.picker.upload'), primary: true },
      ],
    });
    if (r.id !== 'ok') return;
    try {
      const items = await uploadFiles(sel.value, files);
      await this.navigate(sel.value);
      this.selected = new Set(items.map((i) => i.id));
      this.list.querySelectorAll<HTMLElement>('tr[data-id]').forEach((row) => row.classList.toggle('selected', this.selected.has(row.dataset['id'] ?? '')));
      this.okBtn.disabled = this.selected.size === 0;
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  }

  private installDrop(): void {
    this.list.addEventListener('dragover', (e) => {
      if (e.dataTransfer?.types.includes('Files')) e.preventDefault();
    });
    dropHighlight(this.list, (e) => Boolean(e.dataTransfer?.types.includes('Files')));
    this.list.addEventListener('drop', (e) => {
      if (e.dataTransfer?.files.length) {
        e.preventDefault();
        void this.uploadDialog(e.dataTransfer.files);
      }
    });
  }

  focus(): void {
    this.list.focus();
  }

  destroy(): void {
    this.layoutObserver.disconnect();
    void this.win;
  }
}
